import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AsyncStateStatus, useAsyncState } from '../helpers/useAsyncState';
import { Link } from 'react-router-dom';
import { useGraphStorageApi } from '../api/useGraphStorageApi';
import { GraphIcon } from './GraphIcon';
import { GraphDescription } from './types';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trash2, Pencil, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SortOption } from './GraphListLayout';

function getColorForId(id: string) {
    const value = id.split('').reduce((acc: number, currentCharacter: string) => acc + currentCharacter.charCodeAt(0), 0);
    return `hsl(${value % 360}, 70%, 55%)`;
}

function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
        return 'Today';
    } else if (diffDays === 1) {
        return 'Yesterday';
    } else if (diffDays < 7) {
        return `${diffDays} days ago`;
    } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
}

interface Props {
    sortBy: SortOption;
    onCreateGraph?: () => void;
}

export const GraphList: React.FC<Props> = ({ sortBy, onCreateGraph }) => {
    const { data, status, setSuccess, setPending, setError } = useAsyncState<GraphDescription[]>([]);
    const { getAllGraphDescriptions, deleteGraph: deleteGraphApi, renameGraph: renameGraphApi } = useGraphStorageApi();
    const [refreshCounter, setRefreshCounter] = useState<number>(0);
    const [hoveredGraphId, setHoveredGraphId] = useState<string | undefined>(undefined);
    const [editingGraphId, setEditingGraphId] = useState<string | undefined>(undefined);
    const [editingName, setEditingName] = useState<string>('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [graphToDelete, setGraphToDelete] = useState<GraphDescription | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        let requestAborted = false;
        const fetchData = async () => {
            setPending();
            try {
                const graphList = await getAllGraphDescriptions();
                if (!requestAborted) {
                    setSuccess(graphList);
                }
            } catch (e: any) {
                if (!requestAborted) {
                    setError(e);
                }
            }
        };
        fetchData();
        return () => {
            requestAborted = true;
        };
    }, [getAllGraphDescriptions, setError, setPending, setSuccess, refreshCounter]);

    useEffect(() => {
        if (editingGraphId && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editingGraphId]);

    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => {
            const dateA = new Date(sortBy === 'updatedAt' ? a.updatedAt : a.createdAt).getTime();
            const dateB = new Date(sortBy === 'updatedAt' ? b.updatedAt : b.createdAt).getTime();
            return dateB - dateA;
        });
    }, [data, sortBy]);

    const handleDeleteClick = (e: React.MouseEvent, graph: GraphDescription) => {
        e.preventDefault();
        e.stopPropagation();
        setGraphToDelete(graph);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!graphToDelete) return;
        setIsDeleting(true);
        try {
            await deleteGraphApi(graphToDelete.id);
            setDeleteDialogOpen(false);
            setGraphToDelete(null);
            setRefreshCounter(c => c + 1);
        } catch (error) {
            console.error('Failed to delete graph:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleRenameSubmit = async (graphId: string) => {
        if (editingName.trim() && editingName !== data.find(g => g.id === graphId)?.metadata.name) {
            try {
                await renameGraphApi(graphId, editingName.trim());
                setRefreshCounter(c => c + 1);
            } catch (error) {
                console.error('Failed to rename graph:', error);
            }
        }
        setEditingGraphId(undefined);
        setEditingName('');
    };

    const handleRenameKeyDown = (e: React.KeyboardEvent, graphId: string) => {
        if (e.key === 'Enter') {
            handleRenameSubmit(graphId);
        } else if (e.key === 'Escape') {
            setEditingGraphId(undefined);
            setEditingName('');
        }
    };

    const renderContent = () => {
        switch (status) {
            case AsyncStateStatus.SUCCESS: {
                if (sortedData.length > 0) {
                    return (
                        <div
                            className="p-6 grid gap-6"
                            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 480px))' }}
                        >
                            {sortedData.map(graphDescription => {
                                const isHovered = graphDescription.id === hoveredGraphId;
                                const isEditing = graphDescription.id === editingGraphId;
                                const color = getColorForId(graphDescription.id);

                                return (
                                    <Link
                                        key={graphDescription.id}
                                        to={`/graph/${graphDescription.id}`}
                                        className="no-underline"
                                        onClick={e => {
                                            if (isEditing) {
                                                e.preventDefault();
                                            }
                                        }}
                                    >
                                        <Card
                                            className={cn(
                                                'relative overflow-hidden transition-all duration-200 cursor-pointer h-[240px]',
                                                'border-l-4 hover:shadow-lg hover:scale-[1.02]',
                                            )}
                                            style={{ borderLeftColor: color }}
                                            onMouseEnter={() => setHoveredGraphId(graphDescription.id)}
                                            onMouseLeave={() => setHoveredGraphId(undefined)}
                                        >
                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                <GraphIcon
                                                    animated={isHovered}
                                                    size={180}
                                                    color={color.replace('hsl(', 'hsla(').replace(')', ', 0.18)')}
                                                    animationDuration="2s"
                                                />
                                            </div>
                                            {isHovered && !isEditing && (
                                                <div className="absolute top-2 right-2 flex gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent"
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setEditingGraphId(graphDescription.id);
                                                            setEditingName(graphDescription.metadata.name);
                                                        }}
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                                        onClick={e => handleDeleteClick(e, graphDescription)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            )}

                                            <div className="p-4 flex flex-col h-full">
                                                <div className="flex items-start gap-3 mb-3">
                                                    <div className="flex-1 min-w-0">
                                                        {isEditing ? (
                                                            <Input
                                                                ref={inputRef}
                                                                value={editingName}
                                                                onChange={e => setEditingName(e.target.value)}
                                                                onBlur={() => handleRenameSubmit(graphDescription.id)}
                                                                onKeyDown={e => handleRenameKeyDown(e, graphDescription.id)}
                                                                className="h-7 text-sm font-semibold"
                                                                onClick={e => e.preventDefault()}
                                                            />
                                                        ) : (
                                                            <h3 className="font-semibold text-foreground truncate">
                                                                {graphDescription.metadata.name}
                                                            </h3>
                                                        )}
                                                    </div>
                                                </div>

                                                {graphDescription.metadata.description && (
                                                    <p className="text-sm text-muted-foreground line-clamp-2 mb-auto">
                                                        {graphDescription.metadata.description}
                                                    </p>
                                                )}

                                                <div className="mt-auto pt-3 border-t border-border">
                                                    <div className="flex justify-between text-xs text-muted-foreground">
                                                        <span>Created {formatDate(graphDescription.createdAt)}</span>
                                                        <span>Updated {formatDate(graphDescription.updatedAt)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </Link>
                                );
                            })}
                            {onCreateGraph && (
                                <Card
                                    className={cn(
                                        'relative overflow-hidden transition-all duration-200 cursor-pointer h-[240px]',
                                        'border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 hover:shadow-lg hover:scale-[1.02]',
                                        'flex items-center justify-center',
                                    )}
                                    onClick={onCreateGraph}
                                >
                                    <div className="flex flex-col items-center gap-3 text-muted-foreground">
                                        <Plus className="h-10 w-10" />
                                        <span className="text-sm font-medium">Create Workflow</span>
                                    </div>
                                </Card>
                            )}
                        </div>
                    );
                } else {
                    return (
                        <div className="flex flex-col justify-center items-center h-full pb-4 animate-in fade-in duration-500">
                            {onCreateGraph ? (
                                <div className="flex flex-col items-center gap-8 pt-8">
                                    <p className="text-lg text-muted-foreground">This is where your workflows go</p>
                                    <Card
                                        className={cn(
                                            'transition-all duration-200 cursor-pointer w-[280px] h-[180px]',
                                            'border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 hover:shadow-lg hover:scale-[1.02]',
                                            'flex items-center justify-center',
                                        )}
                                        onClick={onCreateGraph}
                                    >
                                        <div className="flex flex-col items-center gap-3 text-muted-foreground">
                                            <Plus className="h-10 w-10" />
                                            <span className="text-sm font-medium">Create Workflow</span>
                                        </div>
                                    </Card>
                                </div>
                            ) : (
                                <p className="text-lg text-muted-foreground">This is where your workflows go</p>
                            )}
                        </div>
                    );
                }
            }
            case AsyncStateStatus.INITIAL:
            case AsyncStateStatus.PENDING: {
                return (
                    <div className="flex flex-col justify-center items-center h-full animate-in fade-in duration-1000">
                        <GraphIcon animated size={300} color="rgb(156, 163, 175)" />
                    </div>
                );
            }
        }
    };

    return (
        <>
            <div className="bg-background overflow-y-auto h-full">{renderContent()}</div>

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Workflow</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete "{graphToDelete?.metadata.name}"? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
